import * as React from 'react';

type LazyCdsIconProps = {
  name: string;
  size?: 'small' | 'medium' | 'large';
};

type IconComponent = React.ComponentType<{ size?: 'small' | 'medium' | 'large' }>;

const NullIcon = () => null;

const lazyIconsByName = new Map<string, React.LazyExoticComponent<IconComponent>>();

// Loads the icon library in a separate chunk the first time any icon is rendered.
const getLazyIcon = (name: string) => {
  let LazyIcon = lazyIconsByName.get(name);

  if (!LazyIcon) {
    LazyIcon = React.lazy(() =>
      import(/* webpackChunkName: "cds-icons" */ '@coursera/cds-icons').then((icons) => {
        const icon: unknown = (icons as Record<string, unknown>)[name];

        return { default: typeof icon === 'function' || typeof icon === 'object' ? (icon as IconComponent) : NullIcon };
      })
    );
    lazyIconsByName.set(name, LazyIcon);
  }

  return LazyIcon;
};

/** Renders a CDS icon by name, loading the icon library on demand. Renders nothing for unknown names. */
const LazyCdsIcon = ({ name, size }: LazyCdsIconProps) => {
  const Icon = getLazyIcon(name);

  return (
    <React.Suspense fallback={null}>
      <Icon size={size} />
    </React.Suspense>
  );
};

export default LazyCdsIcon;
